/**
 * 受領・検収のページ /a/:token（#266 1b、docs/96 §5.2・Figma ④ 267:53〜）：{customer}がログインせずに{deliveryNote}の「受領」・{acceptance}の「検収」を押す1枚
 * （承諾ページ /o/・確認ページ /n/ と同じ土台）。アプリの上の帯とメニューは出さない（ログイン前の画面と同じくライト・製品の既定の色）。スマホ前提＝縦1列・押す所 56px
 *   頭：「送信元：自社名」・題（帳票の名前）・番号・件名・合計（税抜）・日付 → 「PDF を保存」 → 明細の要約（8行まで。多ければ「ほか n行」）
 *   下に固定：［受領］／［検収］と一文「「検収」を押すと、記載の内容を確認したことが日時とともに記録され、送信元に通知されます」
 *   押したあと：題の右に「受領済 MM/DD」・「受領済（MM/DD HH:MM）」・記録番号。書面で記録した帳票は「受領済（書面・MM/DD）」（押せない）
 *   無効・取消し・期限切れ（410）は止めの画面（発行した会社名）。無い鍵（404）は「ページが見つかりません」
 *   送った会社でログイン中の人は確かめ用に見るだけ（押せない＝API も 403）
 */
import { ACCEPTANCE_PUBLIC_MSG as M, STOP_MSG, type AcceptanceConfirmResult, type AcceptancePublicPage as Page } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { StopScreen } from "../../components/StopScreen.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { PRELOGIN_PRODUCT, productInfo } from "../../lib/product.js";

class AckApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: { title?: string; code?: string; company?: string; state?: string },
  ) {
    super(body.title ?? "いま処理できませんでした");
  }
}
/** 公開の API（控えを使わない・リファラーを送らない。Cookie は同じサイトなので送る＝送った会社でログイン中かを API が見る） */
async function acall(path: string, method: "GET" | "POST" = "GET"): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/a/${path}`, { method, credentials: "same-origin", referrerPolicy: "no-referrer", cache: "no-store", ...(method === "POST" ? { headers: { "content-type": "application/json" }, body: "{}" } : {}) });
  } catch {
    throw new AckApiError(0, { title: "つながりませんでした。電波の状態を確かめて、もう一度お試しください" });
  }
  if (!res.ok) {
    let b: AckApiError["body"] = {};
    try {
      b = (await res.json()) as AckApiError["body"];
    } catch {
      /* JSON でない答え */
    }
    throw new AckApiError(res.status, b);
  }
  return res;
}

const jst = (iso: string) => new Date(Date.parse(iso) + 9 * 3600 * 1000);
const ymdSlash = (ymd: string) => ymd.replaceAll("-", "/");
const mdTime = (iso: string) => {
  const t = jst(iso);
  return `${t.getUTCMonth() + 1}/${t.getUTCDate()} ${String(t.getUTCHours()).padStart(2, "0")}:${String(t.getUTCMinutes()).padStart(2, "0")}`;
};
const md = (ymd: string) => `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;
const yen = (n: number) => n.toLocaleString("ja-JP");

/** リファラーを送らない・検索に出さない（Caddy も /a/* に付ける。開発の Vite 用に meta も） */
function useNoReferrer(title: string) {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "referrer";
    m.content = "no-referrer";
    const r = document.createElement("meta");
    r.name = "robots";
    r.content = "noindex, nofollow";
    document.head.append(m, r);
    const prev = document.title;
    document.title = title;
    return () => {
      m.remove();
      r.remove();
      document.title = prev;
    };
  }, [title]);
}

export function AcceptancePublicPage() {
  const { token = "" } = useParams();
  const qc = useQueryClient();
  const P = productInfo(PRELOGIN_PRODUCT);
  const key = ["acceptance-page", token];
  const q = useQuery({ queryKey: key, queryFn: async () => (await (await acall(encodeURIComponent(token))).json()) as Page, retry: false, staleTime: Infinity });
  const d = q.data;
  useNoReferrer(d ? `${d.title} ${d.docNo}` : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();

  if (q.isError) {
    const e = q.error instanceof AckApiError ? q.error : null;
    if (e?.status === 410) return <StopScreen art="lock" title={e.body.state === "expired" ? STOP_MSG.acceptanceGone.expiredTitle : STOP_MSG.acceptanceGone.title} body={STOP_MSG.acceptanceGone.body(e.body.company ?? "")} testId="ack-gone" />;
    if (e?.status === 404) return <StopScreen art="question" title={STOP_MSG.deliveryNotFound.title} body={STOP_MSG.deliveryNotFound.body} testId="ack-notfound" />;
    return (
      <div className="dview" data-testid="ack-page">
        <ErrorText>{q.error instanceof Error ? q.error.message : String(q.error)}</ErrorText>
      </div>
    );
  }
  if (!d) return <div className="dview"><p className="hint">読み込んでいます…</p></div>;
  const word = d.kind === "accept" ? M.accept : M.receive;
  const confirm = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      const r = (await (await acall(`${encodeURIComponent(token)}/confirm`, "POST")).json()) as AcceptanceConfirmResult;
      const today = new Date(Date.parse(r.at) + 9 * 3600 * 1000).toISOString().slice(0, 10);
      qc.setQueryData(key, { ...d, done: { at: r.at, on: today, recordNo: r.recordNo, method: "electronic" } } satisfies Page);
    } catch (e) {
      if (e instanceof AckApiError && e.status === 409) void q.refetch();
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const done = d.done;
  return (
    <div className="dview oaccept" data-testid="ack-page">
      <header className="dview-head">
        <p className="dview-kicker" data-testid="ack-from">{M.from.replace("{company}", d.company)}</p>
        <div className="row" style={{ alignItems: "center", gap: 8 }}>
          <h1 className="dview-company" data-testid="ack-title">{d.title}</h1>
          {done ? <span className="chip ai" data-testid="ack-done-chip">{M.doneChip.replace("{word}", word).replace("{md}", md(done.on))}</span> : null}
        </div>
        <p className="dview-subject" data-testid="ack-subject">{d.docNo}　{d.subject}</p>
        <p className="oaccept-total">
          <span className="muted">{M.subtotal}</span> <b className="num" data-testid="ack-total">{yen(d.subtotal)}</b> 円
        </p>
        <p className="hint num">{ymdSlash(d.issueDate)}</p>
      </header>
      <div className="row oaccept-links">
        <span className="sp" />
        <a className="btn" href={`/api/v1/a/${encodeURIComponent(token)}/pdf?dl=1`} rel="noreferrer" data-testid="ack-pdf">{M.pdf}</a>
      </div>
      {/* 明細の要約（Figma ④＝紙の見本の代わり。全部は PDF で） */}
      <section className="ack-card" data-testid="ack-lines">
        <h2 className="ack-card-h">{M.lines.replace("{n}", String(d.lineCount))}</h2>
        <table className="ack-lines">
          <tbody>
            {d.lines.map((l, i) => (
              <tr key={i} className={l.role === "group" ? "is-group" : undefined}>
                <td className="nm">{l.name}</td>
                <td className="qt num">{l.quantity != null ? `${l.quantity} ${l.unit}`.trim() : ""}</td>
                <td className="am num">{l.amount != null ? yen(l.amount) : ""}</td>
              </tr>
            ))}
            <tr className="is-total">
              <td className="nm">{M.subtotal}</td>
              <td />
              <td className="am num">{yen(d.subtotal)}</td>
            </tr>
          </tbody>
        </table>
        {d.lineCount > d.lines.length ? <p className="hint">{M.more.replace("{n}", String(d.lineCount - d.lines.length))}</p> : null}
        {d.taxTotal == null ? <p className="hint ack-tax">{M.taxOnInvoice}</p> : null}
      </section>
      {done ? (
        <div className="oaccept-done" data-testid="ack-done">
          <b>{done.method === "paper" ? M.paperDone.replace("{word}", word).replace("{on}", md(done.on)) : M.done.replace("{word}", word).replace("{at}", mdTime(done.at))}</b>
          {done.recordNo ? <span className="muted num" data-testid="ack-record-no">{M.recordNo.replace("{no}", done.recordNo)}</span> : null}
        </div>
      ) : (
        <div className="oaccept-bar" data-testid="ack-bar" data-notice-above="">
          {d.sender ? <p className="hint" data-testid="ack-sender">{M.sender}</p> : null}
          <ErrorText>{err}</ErrorText>
          <button type="button" className="btn primary oaccept-btn" disabled={busy || d.sender} onClick={() => void confirm()} data-testid="ack-confirm">
            {busy ? M.sending : word}
          </button>
          <p className="hint ack-sentence" data-testid="ack-sentence">{M.sentence.replace("{word}", word)}</p>
        </div>
      )}
      <footer className="dview-foot">
        <img src={P.assets.logoHeader} alt={P.name} width={96} height={30} />
      </footer>
    </div>
  );
}
